// The photo steps on the page: which frames' photos a step works on, their cameras as the editor places them now,
// the masks that keep it to the selection, and what the steps found (kept after the macro, so a
// camera's menu can show it on its photo). The pixel work goes to the page's photo pool (exec), a frame per task.
import type { Rigid } from '../align.ts';
import { compose, inverse as inv } from '../align.ts';
import type { CamImages } from '../edit/camimages.ts';
import type { Params } from '../edit/macros.ts';
import { type Dist, type PhotoCam, monotonic, rgbK, splat } from './camgeom.ts';
import type { DetectOpts, FrameFeats } from './feats.ts';
import type { PhotoTask } from './photoworker.ts';
import type { PG } from './jobs.ts';
import type { Edge3 } from './lines3d.ts';
import type { AlignIter, AlignOut, MatchSummary } from './engine.ts';
import type { PoseGraph } from '../edit/springs.ts';
import type { OrigFeatures } from '../format.ts';
import { type RtabPairs, type RtabScore, rtabPairs, rtabScore } from './rtabcheck.ts';
import { type PlumbOut, plumbLine } from './distort.ts';
import { type EdgeMatch, confirmedSegs, matchEdges } from './edgematch.ts';

/** A scan as the photo steps see it. */
export interface PhotoScan {
  id: string; name: string;
  images: CamImages | null;
  /** Where frame f is drawn now: T · E(f). */
  place(f: number): Rigid;
  /** Its frames held where they are (tree.lock), and RTAB-Map's links between its frames. */
  locked?: Set<number>; links?: PoseGraph | null;
  /** RTAB-Map's keypoints and words (prep/features.py), to check an alignment by. */
  features?: OrigFeatures | null;
  /** World positions (as the macro's clouds) and each point's frame. */
  P: Float32Array; frame: Uint32Array | null;
  /** Selected, and shown, per point. */
  sel: Uint8Array; live: Uint8Array;
}

/** How a photo step went: what to say, or why it couldn't. */
export type StepEnd = { say?: string; warn?: boolean; fail?: string };
export type Exec = (t: PhotoTask) => Promise<FrameFeats>;
/** A calibration is used when it has this many edges, k1 is known to this (1σ), and it makes them this much straighter (rms after / before). */
export const CALIB_MIN = { edges: 20, sd: 0.02, gain: 0.9 };
export type Progress = (text: string, frac: number) => void;

export class PhotoRun {
  /** Per frame ('<scan>:<f>'), what Photo features found, and the camera as it was then. */
  feats = new Map<string, FrameFeats>();
  cams = new Map<string, PhotoCam>();
  /** Per scan, its lens distortion (Calibrate). */
  calib = new Map<string, Dist>();

  /** Frame f's camera, placed as the editor draws it now. */
  camOf(ps: PhotoScan, f: number): PhotoCam | null {
    const ci = ps.images, c = ci?.cams.get(f);
    if (!ci || !c) return null;
    return {
      key: `${ps.id}:${f}`, scan: ps.id, f, w: ci.w, h: ci.h, dw: ci.dw, dh: ci.dh,
      K: rgbK(c.K, ci.w, ci.h, ci.dw, ci.dh), Kd: c.K, P: compose(ps.place(f), c.pose), dist: this.calib.get(ps.id) ?? null,
    };
  }

  /** The frames (with a photo) a step on `on` works on: of the selected points, or of every shown point. */
  framesOf(ps: PhotoScan, on: string): number[] {
    const F = ps.frame, ci = ps.images;
    if (!F || !ci) return [];
    const fr = new Set<number>();
    for (let i = 0; i < F.length; i++) {
      if (!F[i] || !ps.live[i]) continue;
      if (on !== 'sel' || ps.sel[i]) fr.add(F[i]);
    }
    return [...fr].filter(f => ci.cams.has(f)).sort((a, b) => a - b);
  }

  /**
   * Photo features: corners and lines of each frame's photo, kept to its pixels of the selected points (on 'sel'),
   * or whole (on 'all').
   */
  async features(scans: PhotoScan[], p: Params, base: string, exec: Exec, stop: AbortSignal, progress: Progress): Promise<StepEnd> {
    const on = String(p.on), o: DetectOpts = { what: p.what as DetectOpts['what'], n: +p.n, len: +p.len, bendLen: 60 };
    const jobs: { ps: PhotoScan; f: number }[] = [];
    let noPhotos = 0;
    for (const ps of scans) { if (!ps.images) { noPhotos++; continue; } for (const f of this.framesOf(ps, on)) jobs.push({ ps, f }); }
    if (!jobs.length) return { fail: noPhotos === scans.length ? 'these scans have no camera images (prep/images.py --scene, then Publish)' : on === 'sel' ? 'select some points of frames with photos first' : 'no frames with photos here' };
    let done = 0, corners = 0, lines = 0, ms = 0;
    progress(`0 of ${jobs.length} photos`, 0);
    const one = async ({ ps, f }: { ps: PhotoScan; f: number }) => {
      const cam = this.camOf(ps, f)!, F = ps.frame!, sel = ps.sel, live = ps.live;
      const mask = on === 'all' ? null : splat(cam, ps.P, F, i => sel[i] & live[i]);
      const url = new URL(ps.images!.url, base).href;
      const ff = await exec({ kind: 'detect', jpg: `${url}${f}.jpg`, d16: `${url}${f}.d16`, cam, mask, o });
      if (stop.aborted) return;
      this.feats.set(cam.key, ff); this.cams.set(cam.key, cam);
      corners += ff.corners?.n ?? 0; lines += ff.segs?.n ?? 0; ms += ff.ms;
      progress(`${++done} of ${jobs.length} photos`, done / jobs.length);
    };
    await Promise.all(jobs.map(one));
    if (stop.aborted) throw new Error('cancelled');
    const what = [o.what !== 'lines' ? `${corners.toLocaleString()} corners` : '', o.what !== 'corners' ? `${lines.toLocaleString()} lines` : ''].filter(Boolean).join(', ');
    return { say: `${jobs.length} photo${jobs.length === 1 ? '' : 's'}: ${what} (${Math.round(ms / jobs.length)} ms a photo)` };
  }

  /** The last matching's summary and 3D edges. */
  matched: MatchSummary | null = null;
  edges: Edge3[] = [];

  /** The frames with features in the shown scans, their cameras placed as now, for the photogrammetry worker. */
  private framesFor(scans: PhotoScan[]) {
    const out: { cam: PhotoCam; ff: FrameFeats }[] = [];
    for (const ps of scans) for (const [key, ff] of this.feats) {
      if (!key.startsWith(ps.id + ':')) continue;
      const cam = this.camOf(ps, +key.slice(ps.id.length + 1));
      if (cam) { this.cams.set(key, cam); out.push({ cam, ff }); }
    }
    return out;
  }

  /** Match the photos' features across frames (on the photogrammetry worker), with the cameras as they are now. */
  async match(scans: PhotoScan[], pg: PG, stop: AbortSignal, progress: Progress): Promise<MatchSummary | string> {
    const fs = this.framesFor(scans);
    if (fs.length < 2) return fs.length ? 'features in only one photo: Photo features on more frames first' : 'no photo features yet: put a Photo features step before it';
    pg.load(fs);
    this.matched = await pg.run({ k: 'match', o: {} }, p => progress(p.text, p.frac), stop);
    return this.matched;
  }

  /** 3D edges: lines matched across photos, triangulated; the steps after it select near them (ctx.photo.edges). */
  async edges3d(scans: PhotoScan[], p: Params, pg: PG, stop: AbortSignal, progress: Progress): Promise<StepEnd & { edges?: Float32Array }> {
    const m = await this.match(scans, pg, stop, (t, f) => progress(t, 0.9 * f));
    if (typeof m === 'string') return { fail: m };
    progress('triangulating', 0.92);
    this.edges = await pg.run({ k: 'edges', views: +p.views, maxRms: 0.01 }, () => {}, stop);
    if (!this.edges.length) return { fail: `no edge seen in ${+p.views} photos (${m.segTracks} line tracks over ${m.pairs} photo pairs)` };
    const E = new Float32Array(6 * this.edges.length);
    this.edges.forEach((e, k) => { E.set(e.A, 6 * k); E.set(e.B, 6 * k + 3); });
    const byDepth = this.edges.filter(e => e.how === 'depth').length;
    return { edges: E, say: `${m.photos} photos, ${m.pairs} pairs, ${m.segMatches.toLocaleString()} line matches: ${this.edges.length} edges${byDepth ? ` (${byDepth} along the camera path, from depth)` : ''}` };
  }

  /** Per scan, the last calibration's result. */
  calibrated = new Map<string, PlumbOut>();

  /**
   * Calibrate lens: per scan, (k1, k2) straightening the long edges of its photos' features (chains of at least
   * len px); with use, the photo steps after it undo it (cameras' dist).
   */
  calibrate(scans: PhotoScan[], p: Params): StepEnd {
    const len = +p.len, says: string[] = [];
    let any = false;
    for (const ps of scans) {
      const edges: Float32Array[] = [];
      let K: number[] | null = null;
      for (const [key, ff] of this.feats) {
        if (!key.startsWith(ps.id + ':')) continue;
        K ??= this.camOf(ps, +key.slice(ps.id.length + 1))?.K ?? null;
        const { off, pts } = ff.bends;
        for (let k = 0; k + 1 < off.length; k++) {
          const e = pts.subarray(2 * off[k], 2 * off[k + 1]);
          if (e.length >= 4 && Math.hypot(e[e.length - 2] - e[0], e[e.length - 1] - e[1]) >= len) edges.push(e);
        }
      }
      if (!K) continue;
      const r = plumbLine(edges, K, { fitK2: !!p.k2 });
      if (!r) { says.push(`${ps.name}: too few long edges (${edges.length})`); continue; }
      any = true;
      this.calibrated.set(ps.id, r);
      // not sure enough (few edges, or too little of the photo's rim on them), or no real bend to take out (the
      // edges hardly straighter: the lens is fine as it is), or a lens that folds the photo back: said, not used
      const rim = Math.hypot(Math.max(K[2], ps.images!.w - K[2]) / K[0], Math.max(K[3], ps.images!.h - K[3]) / K[1]);
      const unsure = r.edges < CALIB_MIN.edges || r.sd[0] > CALIB_MIN.sd ? `not sure enough to use (${r.edges} edges${r.edges < CALIB_MIN.edges ? `, under ${CALIB_MIN.edges}` : ''}; k1 ± ${r.sd[0].toFixed(3)}${r.sd[0] > CALIB_MIN.sd ? `, over ${CALIB_MIN.sd}` : ''}): whole photos (Photo features on all) give it more`
        : r.rms1 > CALIB_MIN.gain * r.rms0 ? `no lens distortion to speak of (the edges only ${r.rms0.toFixed(2)} → ${r.rms1.toFixed(2)} px off straight, over ${r.edges} edges): not used`
        : !monotonic(r.d, 1.25 * rim) ? `k1 ${r.d.k1.toFixed(4)}, k2 ${r.d.k2.toFixed(4)} fold the photo back near its rim: not used (untick "fit k2 too")` : '';
      const d: Dist = { ...r.d, r2max: (1.25 * rim) ** 2 };
      if (p.use && !unsure) this.calib.set(ps.id, d); else this.calib.delete(ps.id);
      if (unsure) { says.push(`${ps.name}: ${unsure}`); continue; }
      says.push(`${ps.name}: k1 ${r.d.k1.toFixed(4)} ± ${r.sd[0].toFixed(4)}${p.k2 ? `, k2 ${r.d.k2.toFixed(4)} ± ${r.sd[1].toFixed(4)}` : ''}; edges ${r.rms0.toFixed(2)} → ${r.rms1.toFixed(2)} px off straight over ${r.edges} edges${r.dropped ? ` (${r.dropped} curved ones left out)` : ''}`);
    }
    if (!any) return { fail: this.feats.size ? `no scan has enough long edges (${len} px or more): try a shorter length, or Photo features on more frames` : 'no photo features yet: put a Photo features step before it' };
    return { say: says.join('; ') };
  }

  /**
   * Align cameras: the frames with features (of the selected points, or all of them) lined up by their matches
   * (on the photogrammetry worker); iter gets each round's world moves per scan and frame, for the page to show.
   */
  async align(scans: PhotoScan[], p: Params, pg: PG, stop: AbortSignal, finish: AbortSignal, progress: Progress,
    iter: (moves: Map<string, Map<number, Rigid>>, it: AlignIter) => void): Promise<StepEnd & { moves?: Map<string, Map<number, Rigid>>; out?: AlignOut }> {
    const fs = this.framesFor(scans);
    if (fs.length < 2) return { fail: fs.length ? 'features in only one photo: Photo features on more frames first' : 'no photo features yet: put a Photo features step before it' };
    const free: string[] = [], locked: string[] = [], links: [string, string, number, number, Rigid?][] = [];
    for (const ps of scans) {
      const want = p.frames === 'sel' ? new Set(this.framesOf(ps, 'sel')) : null;
      for (const { cam } of fs) if (cam.scan === ps.id && (!want || want.has(cam.f))) (ps.locked?.has(cam.f) ? locked : free).push(cam.key);
      // RTAB-Map's measurement of each link as the cameras have it: b's camera at a's · Qa⁻¹ · M · Qb (Q: a frame's camera
      // in its graph pose's frame), so a drifted frame's links pull it back rather than holding it where it drifted
      const g = ps.links, cams = ps.images?.cams;
      for (const l of g?.links ?? []) {
        const ga = g!.pose.get(l.a), gb = g!.pose.get(l.b), ca = cams?.get(l.a), cb = cams?.get(l.b);
        const rel = ga && gb && ca && cb ? compose(inv(compose(inv(ga), ca.pose)), compose(l.M, compose(inv(gb), cb.pose))) : undefined;
        links.push([`${ps.id}:${l.a}`, `${ps.id}:${l.b}`, l.wt, l.wr, rel]);
      }
    }
    if (!free.length) return { fail: locked.length ? 'the frames to align are all locked' : 'none of the selected points\' frames has photo features' };
    pg.load(fs);
    const split = (it: AlignIter) => {
      const m = new Map<string, Map<number, Rigid>>();
      it.keys.forEach((k, i) => { const j = k.lastIndexOf(':'), id = k.slice(0, j); (m.get(id) ?? m.set(id, new Map()).get(id)!).set(+k.slice(j + 1), it.W[i]); });
      return m;
    };
    const out = await pg.run({ k: 'align', o: { free, locked, links, use: p.use as 'both', rematch: !!p.rematch, ba: { iters: +p.iters, depthW: +p.depth, maxMove: +p.max, offSig: +(p.offset ?? 0) } } },
      x => progress(x.text, 0.3 * x.frac), stop, x => { const it = x as AlignIter; progress(`round ${it.it}${p.rematch ? ` (pass ${it.pass})` : ''}: ${it.rmsPx.toFixed(2)} px`, 0.3 + (0.7 * Math.min(it.it, +p.iters)) / +p.iters); iter(split(it), it); }, finish);
    this.matched = out.match;
    const moves = split(out), mm = (W: Rigid, k: string) => { const c = this.cams.get(k)!.P.t, q = [0, 1, 2].map(i => W.R[3 * i] * c[0] + W.R[3 * i + 1] * c[1] + W.R[3 * i + 2] * c[2] + W.t[i] - c[i]); return 1000 * Math.hypot(...q); };
    const shifts = out.keys.map((k, i) => mm(out.W[i], k)).sort((a, b) => a - b);
    return {
      moves, out,
      say: `${free.length} camera${free.length === 1 ? '' : 's'} aligned against ${fs.length - free.length} held, from ${out.match.cornerMatches.toLocaleString()} corner and ${out.match.segMatches.toLocaleString()} line matches: ` +
        `${out.med0.toFixed(2)} → ${out.medPx.toFixed(2)} px (median), median move ${shifts[shifts.length >> 1].toFixed(1)} mm (${out.why})${out.held ? `; ${out.held} held at ${+p.max} m` : ''}${locked.length ? `; ${locked.length} locked left alone` : ''}`,
    };
  }

  /** Per frame, the last Match edges' result (drawn on its photo). */
  edgeMatches = new Map<string, EdgeMatch>();

  /**
   * Match edges: each frame's depth edges against its photo's; the frames' shifts (depth → photo), and per scan the
   * points (of the frames matched) lying on a confirmed depth edge.
   */
  matchEdges(scans: PhotoScan[], p: Params): StepEnd & { matched?: (Uint8Array | null)[] } {
    const o = { tol: +p.tol, search: +p.search, kind: p.kind as 'any' | 'jump' | 'crease' }, shifts: [number, number][] = [];
    let frames = 0, conf = 0, total = 0;
    const matched = scans.map(ps => {
      const segs = new Map<number, { cam: PhotoCam; s: Float32Array }>();
      for (const [key, ff] of this.feats) {
        if (!key.startsWith(ps.id + ':')) continue;
        const f = +key.slice(ps.id.length + 1), cam = this.camOf(ps, f);
        if (!cam || !ff.segs) continue;
        const m = matchEdges(ff, o);
        this.edgeMatches.set(key, m);
        frames++;
        conf += m.confirmed; total += (o.kind !== 'crease' ? ff.depth.jump.n : 0) + (o.kind !== 'jump' ? ff.depth.crease.n : 0);
        if (m.ok) shifts.push(m.shift);
        segs.set(f, { cam, s: confirmedSegs(ff, m) });
      }
      if (!segs.size || !ps.frame) return null;
      // the frames' own points on a confirmed depth edge: projected (as the depth had them) within tol (and a depth pixel)
      const out = new Uint8Array(ps.frame.length), Pi = new Map([...segs].map(([f, x]) => [f, inv(x.cam.P)]));
      for (let i = 0; i < ps.frame.length; i++) {
        const f = ps.frame[i], x = segs.get(f);
        if (!x || !x.s.length || !ps.live[i]) continue;
        const R = Pi.get(f)!, X = ps.P[3 * i], Y = ps.P[3 * i + 1], Z = ps.P[3 * i + 2];
        const qx = R.R[0] * X + R.R[1] * Y + R.R[2] * Z + R.t[0], qy = R.R[3] * X + R.R[4] * Y + R.R[5] * Z + R.t[1], qz = R.R[6] * X + R.R[7] * Y + R.R[8] * Z + R.t[2];
        if (qz <= 0) continue;
        const u = (x.cam.K[0] * qx) / qz + x.cam.K[2], v = (x.cam.K[1] * qy) / qz + x.cam.K[3], tol = o.tol + x.cam.w / x.cam.dw, S = x.s;
        for (let k = 0; k < S.length; k += 4) {
          const dx = S[k + 2] - S[k], dy = S[k + 3] - S[k + 1], L2 = dx * dx + dy * dy, t = L2 ? Math.max(0, Math.min(1, ((u - S[k]) * dx + (v - S[k + 1]) * dy) / L2)) : 0;
          if (Math.hypot(u - S[k] - t * dx, v - S[k + 1] - t * dy) <= tol) { out[i] = 1; break; }
        }
      }
      return out;
    });
    if (!frames) return { fail: 'no photo features yet: put a Photo features step before it' };
    const med = (k: 0 | 1) => { const a = shifts.map(s => s[k]).sort((x, y) => x - y); return a.length ? a[a.length >> 1] : NaN; };
    const mx = -med(0), my = -med(1), far = Math.hypot(mx, my) > 2 * o.tol;   // the depth's offset: minus the photo's
    return {
      matched, warn: far,
      say: `${frames} photo${frames === 1 ? '' : 's'}: ${conf} of ${total} depth edges on a photo edge` +
        (shifts.length ? `; the depth sits (${mx.toFixed(1)}, ${my.toFixed(1)}) px from the photo (median of ${shifts.length} frames)${far ? ': out of register (calibration, or depth and photo not taken together)' : ''}` : ''),
    };
  }

  /** Per scan, its RTAB-Map keypoint pairs (made once). */
  private rtab = new Map<string, RtabPairs>();

  /**
   * How well RTAB-Map's own matches (near frames sharing a word) agree with the cameras now, moved by moves (per scan,
   * per frame): over every scan with features and a pose graph, median px off in the photos and mm apart in depth.
   */
  rtabCheck(scans: PhotoScan[], moves?: Map<string, Map<number, Rigid>>): RtabScore | null {
    let n = 0;
    const px: number[] = [], mm: number[] = [];
    for (const ps of scans) {
      if (!ps.features || !ps.links || !ps.images) continue;
      let pairs = this.rtab.get(ps.id);
      if (!pairs) this.rtab.set(ps.id, (pairs = rtabPairs(ps.features, f => !!ps.images!.cams.has(f))));
      const r = rtabScore(pairs, f => this.camOf(ps, f), f => ps.images!.cams.get(f)?.pose, ps.links, moves?.get(ps.id));
      if (r) { n += r.n; px.push(r.px); mm.push(r.mm); }
    }
    // per scan medians, weighted alike (a scan's own spread is in its median)
    return n ? { n, px: px.reduce((a, b) => a + b) / px.length, mm: mm.reduce((a, b) => a + b) / mm.length } : null;
  }

  clear() { this.feats.clear(); this.cams.clear(); this.edges = []; this.matched = null; this.edgeMatches.clear(); }
}
